import { Divider } from '@chakra-ui/react';
import { SidebarItem, SidebarList } from '@gamma/ui/navigation';
import DashboardSharpIcon from '@gamma/ui/icons/DashboardSharpIcon';
import FactCheckSharpIcon from '@gamma/ui/icons/FactCheckSharpIcon';
import FrameInspectSharpIcon from '@gamma/ui/icons/FrameInspectSharpIcon';
import IntegrationInstructionsSharpIcon from '@gamma/ui/icons/IntegrationInstructionsSharpIcon';
import ListSharpIcon from '@gamma/ui/icons/ListSharpIcon';
import ManageAccountsSharpIcon from '@gamma/ui/icons/ManageAccountsSharpIcon';
import SensorsSharpIcon from '@gamma/ui/icons/SensorsSharpIcon';
import SettingsSharpIcon from '@gamma/ui/icons/SettingsSharpIcon';
import TargetSharpIcon from '@gamma/ui/icons/TargetSharpIcon';
import { PopUpMenuLinksProps } from '@gamma/ui/navigation';
import moment from 'moment';
import { useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router';
import { DashboardMenu } from '../../../../../features/investigations/components';
import { ROUTES } from '../../../../constants';
import {
  useDateRangeContext,
  useDateRangeQueryString,
} from '../../../../hooks';
import { i18n } from '../../../../localization';
import { AuthContext } from '../../../../providers';

const {
  alertCatalog,
  securityAudit,
  logSearchWithParams,
  adminUsers: adminUsersRoute,
  overviewWithParams: overviewRoute,
  detectionsWithParams: detectionsRoute,
  systemSettingsIntegrations: integrationsRoute,
  systemSettingsIntegrationsConfig: integrationsConfigRoute,
  systemSettingsGeneral: systemSettingsGeneralRoute,
  systemSettingsAlertCatalog: systemSettingsAlertCatalogRoute,
  systemSettingsSecurityAudit: systemSettingsSecurityAuditRoute,
  systemSettingsSensorMonitoring: systemSettingsSensorMonitoringRoute,
} = ROUTES;

const { dashboardOverview, search, detections, system, admin } =
  i18n.navigation;

interface ISidebarLinks {
  areItemsDisabled?: boolean;
}

export const SidebarLinks = ({ areItemsDisabled }: ISidebarLinks) => {
  const { isOrgTenant, userRole } = useContext(AuthContext);

  const [params] = useSearchParams();
  const tenant = params.get('tenant');

  const { start, end } = useDateRangeQueryString();
  const { dateRange, showCustomDateRangePicker } = useDateRangeContext();
  const getStartAndEndParams = useCallback(() => {
    if (dateRange) {
      const end = moment().unix();
      const start = moment().subtract(dateRange[0], dateRange[1]).unix();
      return [start, end];
    }
    return [0, 0];
  }, [dateRange]);
  const [startParam, endParam] = getStartAndEndParams();
  const datesParams = `?start=${start > 0 ? start : startParam}&end=${end > 0 ? end : endParam}&cdate=${showCustomDateRangePicker}`;
  const tenantParam = `tenant=${tenant}`;
  const queryString = `${datesParams}${tenant ? `&${tenantParam}` : ''}`;

  const humioDatesParams = `humioStart=${start * 1000}&humioEnd=${end * 1000}`;

  const alertCatalogLink = {
    to: systemSettingsAlertCatalogRoute(`${tenant ? `?${tenantParam}` : ''}`),
    label: system.alertCatalog,
    icon: <TargetSharpIcon boxSize={5} />,
    rootPath: alertCatalog,
  };

  const systemSettingsSensorMonitoringLink = {
    to: systemSettingsSensorMonitoringRoute,
    label: system.sensorMonitoring,
    icon: <SensorsSharpIcon />,
  };

  const integrationsConfigLink = {
    to: integrationsConfigRoute,
    label: system.integrations,
    rootPath: integrationsRoute,
    icon: <IntegrationInstructionsSharpIcon />,
  };

  const adminSystemAndUserMenuLinks: PopUpMenuLinksProps[] = [
    {
      to: systemSettingsGeneralRoute,
      label: system.generalSettings,
      icon: <SettingsSharpIcon />,
    },
    alertCatalogLink,
    ...(!isOrgTenant ? [integrationsConfigLink] : []),
    {
      to: systemSettingsSecurityAuditRoute(
        `${tenant ? `?${tenantParam}` : ''}`,
      ),
      label: system.securityAudit,
      icon: <FactCheckSharpIcon />,
      rootPath: securityAudit,
    },
    systemSettingsSensorMonitoringLink,
    {
      to: adminUsersRoute,
      label: admin.users,
      icon: <ManageAccountsSharpIcon />,
      hasDivider: true,
    },
  ];

  const analystSystemAndUserMenuLinks: PopUpMenuLinksProps[] = [
    alertCatalogLink,
    ...(!isOrgTenant ? [integrationsConfigLink] : []),
    systemSettingsSensorMonitoringLink,
  ];

  const systemAndUserMenuLinks =
    userRole === 'admin'
      ? adminSystemAndUserMenuLinks
      : analystSystemAndUserMenuLinks;

  return (
    <>
      <SidebarItem
        exact={false}
        icon={<DashboardSharpIcon />}
        isDisabled={areItemsDisabled}
        to={overviewRoute(queryString)}
        data-testid="sidebar-link-overview"
        popoverContent={<DashboardMenu />}
      >
        {dashboardOverview}
      </SidebarItem>

      <SidebarItem
        exact={false}
        icon={<ListSharpIcon />}
        isDisabled={areItemsDisabled}
        to={detectionsRoute(queryString)}
        data-testid="sidebar-link-detections"
      >
        {detections}
      </SidebarItem>

      <SidebarItem
        exact={false}
        isDisabled={areItemsDisabled}
        icon={<FrameInspectSharpIcon />}
        data-testid="sidebar-link-log-search"
        to={logSearchWithParams(`${datesParams}&${humioDatesParams}`)}
      >
        {search}
      </SidebarItem>

      <Divider mb={2} />

      <SidebarList
        label={system.settings}
        isDisabled={areItemsDisabled}
        links={systemAndUserMenuLinks}
        icon={<SettingsSharpIcon />}
        data-testid="sidebar-link-system-and-user"
      />
    </>
  );
};
